import * as Switch from "@radix-ui/react-switch";
import classNames from "classnames";
import { useState } from "react";
import KuikaText from "../typography/Text/Text";
import "./kuika-switch.scss";

const KuikaSwitch = ({
  label,
  checked: controlledChecked,
  onChange,
  disabled,
  defaultChecked,
  align = "center",
  labelPosition = "right",
  dataTestId,
  labelTestId,
  onClick,
  className,
  id,
  ...rest
}: {
  label?: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  defaultChecked?: boolean;
  align?: "center" | "top";
  labelPosition?: "right" | "left" | "none";
  /** Forwarded onto the toggle control itself (not the wrapper) so tests can target the clickable element. */
  dataTestId?: string;
  /** On the label text. Only worth passing where a suite reads the wording, not the state. */
  labelTestId?: string;
  /** On the wrapper (track and label), which is also where a press on a disabled switch lands. */
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /** Merged onto the wrapper alongside its own layout classes, rather than replacing them. */
  className?: string;
  /** Forwarded onto the toggle control itself (not the wrapper), so a `<label htmlFor>` targets
   * the focusable element — `KuikaFormField` clones this in from the form store. */
  id?: string;
  [key: string]: any;
}) => {
  const [internalChecked, setInternalChecked] = useState(defaultChecked || false);
  const isControlled = controlledChecked !== undefined;
  const checked = isControlled ? controlledChecked : internalChecked;

  const handleCheckedChange = (newChecked: boolean) => {
    if (!isControlled) {
      setInternalChecked(newChecked);
    }
    if (onChange) {
      onChange(newChecked);
    }
  };

  // A disabled Radix switch is a disabled <button>, which never receives a click. The stylesheet
  // lets presses fall through it to the wrapper, and the wrapper answers a press on a disabled
  // switch with a small shake — the same refusal an aria-disabled KuikaButton gives.
  const [isDenied, setIsDenied] = useState<boolean>(false);

  const handleWrapperClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (disabled) {
      setIsDenied(true);
    }
    onClick?.(event);
  };

  const handleRootAnimationEnd = (event: React.AnimationEvent<HTMLButtonElement>): void => {
    // The thumb's own transitions and animations bubble here too; only the switch's shake ends the refusal.
    if (event.target === event.currentTarget) {
      setIsDenied(false);
    }
  };

  const wrapperClassName = classNames(
    "kuika-switch",
    {
      "kuika-switch--center": align === "center",
      "kuika-switch--top": align === "top",
      "kuika-switch--disabled": disabled
    },
    className
  );

  return (
    <div {...rest} className={wrapperClassName} onClick={handleWrapperClick}>
      {labelPosition === "left" && (
        <KuikaText style={{ marginRight: 8 }} data-testid={labelTestId}>
          {label}
        </KuikaText>
      )}
      <Switch.Root
        id={id}
        // A disabled switch still has to say which way it is set — the `.scss`
        // dims it rather than drawing it as off, so the class follows `checked`
        // alone.
        className={classNames("kuika-switch__root", {
          "kuika-switch--checked": checked,
          "is-denied": isDenied
        })}
        checked={checked}
        onCheckedChange={handleCheckedChange}
        disabled={disabled}
        onAnimationEnd={handleRootAnimationEnd}
        data-testid={dataTestId}
      >
        <Switch.Thumb className="kuika-switch__thumb" />
      </Switch.Root>
      {labelPosition === "right" && (
        <KuikaText style={{ marginLeft: 8 }} data-testid={labelTestId}>
          {label}
        </KuikaText>
      )}
    </div>
  );
};

export default KuikaSwitch;
